import { requireRecycleSuccess } from '@/lib/recycleErrors';
// ─────────────────────────────────────────────
// KLOTHIQ — Cash Accounts hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import type { Database } from '@klothiq/domain';

export type CashAccount = Database['public']['Tables']['cash_accounts']['Row'];
export type CashTransaction = Database['public']['Tables']['cash_transactions']['Row'];

export const CASH_KEY = 'cash_accounts';
export const CASH_TXN_KEY = 'cash_transactions';
export const CASH_VOIDED_KEY = 'voided_cash_transactions';

export type CashTransactionWithAccount = CashTransaction & {
  cash_accounts?: { name: string } | null;
};

// ── List accounts ─────────────────────────────────────────────
export function useCashAccounts() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CASH_KEY, orgId],
    queryFn: async (): Promise<CashAccount[]> => {
      const { data, error } = await supabase
        .from('cash_accounts')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60,
  });
}

// ── Total cash balance across all accounts ────────────────────
export function useTotalCashBalance() {
  const accounts = useCashAccounts();
  const total = (accounts.data ?? []).reduce((sum, a) => sum + Number(a.current_balance), 0);
  return { ...accounts, total };
}

// ── KLOTHIQ Bank resolution (mirrors SQL acc_klothiq_bank_id) ──
// Priority: exact name → name contains "klothiq" + "bank" → oldest bank-type account.
// Never falls back to a cash/UPI account, since those hold sales money.
export function findKlothiqBank(accounts: CashAccount[] | undefined | null): CashAccount | null {
  if (!accounts || accounts.length === 0) return null;
  const rank = (a: CashAccount) =>
    a.name === 'KLOTHIQ Bank'
      ? 1
      : /klothiq.*bank/i.test(a.name)
        ? 2
        : a.account_type === 'bank'
          ? 3
          : 99;
  const candidates = accounts
    .filter((a) => rank(a) < 99)
    .sort((x, y) => rank(x) - rank(y) || x.created_at.localeCompare(y.created_at));
  return candidates[0] ?? null;
}

/** Splits balances into KLOTHIQ Bank vs. everything else (Main Cash / UPI = sales money). */
export function splitBalances(accounts: CashAccount[] | undefined | null) {
  const list = accounts ?? [];
  const bank = findKlothiqBank(list);
  const bankBalance = Number(bank?.current_balance ?? 0);
  const others = list.filter((a) => a.id !== bank?.id);
  const salesBalance = others.reduce((s, a) => s + Number(a.current_balance), 0);
  return { bank, bankBalance, others, salesBalance, total: bankBalance + salesBalance };
}

// ── Transactions for a specific account ──────────────────────
export function useCashTransactions(accountId?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CASH_TXN_KEY, orgId, accountId],
    queryFn: async (): Promise<CashTransaction[]> => {
      const { data, error } = await supabase
        .from('cash_transactions')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('account_id', accountId!)
        .eq('is_void', false)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId && !!accountId,
    staleTime: 1000 * 30,
  });
}

// ── Create cash account ───────────────────────────────────────
export function useCreateCashAccount() {
  const queryClient = useQueryClient();
  const { organization, profile } = useAuth();

  return useMutation({
    mutationFn: async (values: {
      name: string;
      account_type: 'cash' | 'bank' | 'upi' | 'other';
      opening_balance?: number;
    }) => {
      const bal = values.opening_balance ?? 0;
      let orgId = organization?.id;
      if (!orgId) {
        const { data: firstOrg } = await supabase
          .from('organizations')
          .select('id')
          .limit(1)
          .single();
        orgId = firstOrg?.id;
      }
      if (!orgId) throw new Error('Organization not found. Please log in again.');

      const { data, error } = await supabase
        .from('cash_accounts')
        .insert({
          ...values,
          opening_balance: bal,
          current_balance: bal,
          organization_id: orgId,
          created_by: profile?.id,
          updated_by: profile?.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CASH_KEY] }),
  });
}

// ── Transfer cash via RPC ────────────────────────────────────
export function useTransferCash() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      from_account_id: string;
      to_account_id: string;
      amount: number;
      transfer_date: string;
    }) => {
      const { data, error } = await supabase.rpc('transfer_cash', { p_data: input });
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Record expense via RPC ────────────────────────────────────
export function useRecordExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      account_id: string;
      amount: number;
      particular: string;
      expense_date?: string;
      date?: string;
      expense_category_id?: string | null;
      payment_method?: string | null;
      notes?: string | null;
    }) => {
      const { data, error } = await supabase.rpc('record_expense', { p_data: input });
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Delete / Archive Cash Account via RPC ─────────────────────
export function useDeleteCashAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { account_id: string }) => {
      const { data, error } = await supabase.rpc('delete_cash_account', { p_data: params });
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Void / Delete Cash Transaction via RPC ────────────────────
export function useVoidCashTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { transaction_id: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('void_cash_transaction', { p_data: params });
      if (error) throw error;
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Query: List voided / deleted cash transactions for Recycle Bin ─────
export function useVoidedCashTransactions() {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CASH_VOIDED_KEY, orgId],
    queryFn: async (): Promise<CashTransactionWithAccount[]> => {
      const { data, error } = await supabase
        .from('cash_transactions')
        .select(`*, cash_accounts(name)`)
        .eq('organization_id', orgId!)
        .eq('is_void', true)
        .order('voided_at', { ascending: false });
      if (error) throw error;
      return (data as CashTransactionWithAccount[]) ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 15,
  });
}

// ── Restore voided cash transaction via RPC ────────────────────
export function useRestoreCashTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { transaction_id: string }) => {
      const { data, error } = await supabase.rpc('restore_cash_transaction', { p_data: params });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}

// ── Permanently delete voided cash transaction via RPC ─────────
export function usePermanentDeleteCashTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { transaction_id: string }) => {
      const { data, error } = await supabase.rpc('permanently_delete_cash_transaction', {
        p_data: params,
      });
      requireRecycleSuccess(data, error);
      return data;
    },
    onSuccess: () => refreshSaleConsumers(queryClient),
  });
}
